import { useEffect, useId, useRef, useState, type CSSProperties } from 'react';
import { flushInputDrafts, normalizeNumericDraft, registerInputDraft } from './input-drafts';

export const WEIGHT_MARKS = [
  { value: 0, label: '不参与' }, { value: 25, label: '较低' },
  { value: 50, label: '一般' }, { value: 75, label: '优先' }, { value: 100, label: '重点' },
] as const;

// Describe intermediate values without rounding or replacing the stored weight.
function describeWeight(value: number) {
  if (value === 0) return WEIGHT_MARKS[0].label;
  return WEIGHT_MARKS[Math.max(1, Math.round(value / 25))]!.label;
}

export function WeightControl({ name, value, onChange }: { name: string; value: number; onChange(value: number): void }) {
  const id = useId();
  const [text, setText] = useState(String(value));
  const draft = useRef(String(value));
  const changed = useRef(false);
  const dragging = useRef(false);
  const adjustingKey = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const unregister = useRef<(() => void) | undefined>(undefined);
  const latest = useRef({ value, onChange });
  latest.current = { value, onChange };
  useEffect(() => {
    if (!changed.current) { draft.current = String(value); setText(String(value)); }
  }, [value]);
  useEffect(() => () => { clearTimeout(timer.current); unregister.current?.(); }, []);
  const commit = () => {
    clearTimeout(timer.current); unregister.current?.(); unregister.current = undefined;
    if (!changed.current) return;
    changed.current = false;
    const next = normalizeNumericDraft(draft.current, latest.current.value, 0, 100);
    draft.current = String(next); setText(String(next));
    if (next !== latest.current.value) latest.current.onChange(next);
  };
  const edit = (next: string, defer: boolean) => {
    draft.current = next; changed.current = true; setText(next);
    clearTimeout(timer.current); unregister.current?.(); unregister.current = registerInputDraft(commit);
    if (defer && next.trim() && Number.isFinite(Number(next))) timer.current = setTimeout(commit, 250);
  };
  const finishDrag = () => { dragging.current = false; adjustingKey.current = false; commit(); };
  const cancel = () => {
    clearTimeout(timer.current); unregister.current?.(); unregister.current = undefined;
    changed.current = false; dragging.current = false; adjustingKey.current = false;
    draft.current = String(latest.current.value); setText(draft.current);
  };
  const shown = normalizeNumericDraft(text, value, 0, 100);
  const level = describeWeight(shown);
  return <div className="weight-field" data-zero={shown === 0 || undefined}>
    <label className="weight-name" htmlFor={`${id}-number`} title={name}>{name}</label>
    <label className="weight-value" htmlFor={`${id}-number`} title="点击数值精调，范围 0–100">
      <span className="weight-level" aria-hidden="true">{level}</span>
      <input id={`${id}-number`} type="number" inputMode="numeric" min={0} max={100} step={1}
        value={text} aria-label={`${name}权重数值`}
        onChange={event => edit(event.currentTarget.value, true)} onBlur={commit}
        onFocus={event => event.currentTarget.select()}
        onKeyDown={event => {
          if (event.key === 'Enter') event.currentTarget.blur();
          if (event.key === 'Escape') { cancel(); event.currentTarget.blur(); }
        }} />
    </label>
    <div className="weight-rail" style={{ '--weight-fill': `${shown}%` } as CSSProperties}>
      <input type="range" min={0} max={100} step={1} value={shown} list={`${id}-marks`}
        aria-label={`${name}重视程度`} aria-valuetext={`${level}，权重 ${shown}`}
        title={`${name}：${level}（${shown}）；点击或拖动调整，方向键精调`}
        onPointerDown={() => { flushInputDrafts(); dragging.current = true; }}
        onChange={event => edit(event.currentTarget.value, !dragging.current && !adjustingKey.current)}
        onPointerUp={finishDrag} onPointerCancel={cancel} onLostPointerCapture={finishDrag}
        onBlur={finishDrag}
        onKeyUp={event => { if (/^(Arrow|Home|End|Page)/.test(event.key)) { adjustingKey.current = false; commit(); } }}
        onKeyDown={event => {
          if (/^(Arrow|Home|End|Page)/.test(event.key)) adjustingKey.current = true;
          if (event.key === 'Escape') { event.preventDefault(); cancel(); }
        }} />
      <datalist id={`${id}-marks`}>{WEIGHT_MARKS.map(mark => <option key={mark.value} value={mark.value} label={mark.label} />)}</datalist>
      <span className="weight-ticks" aria-hidden="true">{WEIGHT_MARKS.map(mark => <i key={mark.value} />)}</span>
    </div>
  </div>;
}
